import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Map, Package, Mail, FilePlus, ArrowRight, TrendingUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminStats().then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const kpis = [
    { label: "Itineraries", value: stats?.total_itineraries || 0, sub: `${stats?.draft_itineraries || 0} drafts`, icon: Map, color: "from-[#A855F7] to-[#D946EF]" },
    { label: "Tour Packages", value: stats?.total_packages || 0, sub: `${stats?.active_packages || 0} active`, icon: Package, color: "from-[#22C55E] to-[#10B981]" },
    { label: "Inquiries", value: stats?.total_inquiries || 0, sub: `${stats?.new_inquiries || 0} new`, icon: Mail, color: "from-[#3B82F6] to-[#06B6D4]" },
    { label: "Activity", value: "↑", sub: "Real-time", icon: TrendingUp, color: "from-[#F59E0B] to-[#EF4444]" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-800 drop-shadow-sm">Welcome back, Lucky 👋</h1>
          <p className="text-slate-500 mt-1">Here's a quick overview of your Happy Journey 4 U.</p>
        </div>
        <Link to="/admin/itineraries/new"><Button className="rounded-xl btn-luxe  w-full sm:w-auto" data-testid="dashboard-new-itinerary-button"><FilePlus className="w-4 h-4 mr-2" />New Itinerary</Button></Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5 rounded-2xl glass-panel-dark border-slate-200 hover:-translate-y-1 transition-transform duration-300">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${k.color} flex items-center justify-center mb-3 shadow-[0_0_15px_rgba(255,255,255,0.1)]`}>
              <k.icon className="w-5 h-5 text-slate-800" />
            </div>
            <div className="text-3xl font-display font-bold text-slate-800">{k.value}</div>
            <div className="text-xs text-slate-500">{k.label} · {k.sub}</div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 rounded-2xl glass-panel-dark border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg text-slate-800">Recent Inquiries</h3>
            <Link to="/admin/inquiries" className="text-xs text-orange-500 hover:text-slate-800 transition-colors inline-flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {stats?.recent_inquiries?.length > 0 ? (
            <ul className="space-y-3">
              {stats.recent_inquiries.map((i) => (
                <li key={i.id} className="flex items-start gap-3 py-2 border-b border-slate-200 last:border-0 hover:bg-slate-50 p-2 rounded-lg transition-colors -mx-2">
                  <div className="w-9 h-9 rounded-full bg-slate-100 text-orange-500 border border-slate-200 shadow-inner font-semibold flex items-center justify-center text-sm">{(i.name || "?").charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 text-sm truncate">{i.name}</div>
                    <div className="text-xs text-slate-500 truncate">{i.tour_interest || "General Inquiry"} · {new Date(i.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-blue-300 px-2 py-1 bg-blue-900/40 border border-blue-400/20 rounded-full">{i.status || "new"}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-12 text-center text-slate-500 text-sm">No inquiries yet. Once visitors submit the contact form, they'll appear here.</div>
          )}
        </Card>
        <Card className="p-6 rounded-2xl glass-panel-dark border-slate-200 text-slate-800 relative overflow-hidden shadow-[0_0_30px_rgba(56,189,248,0.1)]">
          <div className="absolute top-[-50px] right-[-50px] w-32 h-32 bg-blue-500/20 rounded-full blur-[40px]"></div>
          <h3 className="font-display font-bold text-xl relative z-10">Quick Actions</h3>
          <p className="text-slate-500 text-sm mt-1 relative z-10">Get started fast.</p>
          <div className="mt-5 space-y-2 relative z-10">
            <Link to="/admin/itineraries/new"><Button variant="secondary" className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-400 hover:to-amber-300 border-none shadow-lg" data-testid="quick-action-new-itinerary">✨ New AI Itinerary</Button></Link>
            <Link to="/admin/packages"><Button variant="secondary" className="w-full rounded-xl bg-slate-50 text-slate-800 hover:bg-slate-100 border-slate-200 border">Manage Tour Packages</Button></Link>
            <Link to="/admin/inquiries"><Button variant="secondary" className="w-full rounded-xl bg-slate-50 text-slate-800 hover:bg-slate-100 border-slate-200 border">View Inquiries</Button></Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
